import { useCallback } from 'react';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import { filter, sort } from 'ramda';
import { useDispatch, useSelector } from 'react-redux';

import Autocomplete from '@gef-ui/form-material/Autocomplete';
import SubmitButton from '@gef-ui/form-material/SubmitButton';
import TextField from '@gef-ui/form-material/TextField';
import { getLabelMessage } from '@gef-ui/localization/messages';

import { allowedCampaignStatesForRole } from 'constants/campaigns';
import { codelists } from 'constants/codelists';
import { MaxLengths } from 'constants/forms';
import { resetCriteria, submitForm } from 'features/views/campaignList/actions';
import { FIELDS } from 'features/views/campaignList/form';
import user from 'features/views/user';
import { getCodelistByName, sortAlphabeticallyBy } from 'utils';

export const CampaignsListForm = () => {
	const dispatch = useDispatch();
	const campaignChannels = useSelector(getCodelistByName(codelists.campaignChannels));
	const campaignStates = useSelector(getCodelistByName(codelists.campaignStates));

	const currentRole = useSelector(user.selectors.getCurrentRole);
	const allowedCampaignStates = allowedCampaignStatesForRole[currentRole];
	const isCampaignManagementScreen = window.location.pathname === '/campaign/management/list';

	const generateSortedChannels = useCallback(
		() => sort(sortAlphabeticallyBy('description'))(campaignChannels),
		[campaignChannels],
	);

	const generateCampaignStatesOptions = useCallback(() => {
		return isCampaignManagementScreen
			? filter((item) => allowedCampaignStates.includes(Number(item.id)))(campaignStates)
			: campaignStates;
	}, [campaignStates, allowedCampaignStates, isCampaignManagementScreen]);

	const handleReset = useCallback(() => {
		dispatch(resetCriteria());
	}, [dispatch]);

	const handleSubmit = useCallback(() => {
		dispatch(submitForm());
	}, [dispatch]);

	return (
		<Grid container>
			<Grid size={12}>
				<Grid container spacing={2}>
					<Grid size={6}>
						<TextField
							size="small"
							fieldName={FIELDS.campaignName}
							label={getLabelMessage('campaignsList:campaignName')}
							slotProps={{
								htmlInput: {
									maxLength: MaxLengths.NAME,
								},
							}}
						/>
					</Grid>
					<Grid size={3}>
						<Autocomplete
							size="small"
							fieldName={FIELDS.campaignState}
							label={getLabelMessage('campaignsList:campaignState')}
							options={generateCampaignStatesOptions()}
							labelPropName="description"
						/>
					</Grid>
					<Grid size={3}>
						<Autocomplete
							size="small"
							fieldName={FIELDS.channel}
							label={getLabelMessage('campaignsList:channel')}
							options={generateSortedChannels()}
							labelPropName="description"
						/>
					</Grid>
					<Grid size={8} />
					<Grid size={2}>
						<Button variant="outlined" onClick={handleReset} fullWidth>
							{getLabelMessage('common:resetButton')}
						</Button>
					</Grid>
					<Grid size={2}>
						<SubmitButton
							variant="contained"
							onClick={handleSubmit}
							fullWidth
							text={getLabelMessage('common:searchButton')}
						/>
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	);
};

export default CampaignsListForm;
